import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { CalendarClock, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MANAGER_SET = new Set(['master_admin', 'admin', 'manager', 'supervisor', 'domain_admin', 'domain_supervisor', 'office_manager']);

const fmtWhen = (iso) => {
  try {
    return new Date(iso).toLocaleString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
  } catch { return iso; }
};

// Compact Executive card: count honours the page's date-range selection,
// the visible list is always capped to the next 7 days (a 90-day "upcoming"
// list isn't useful). Rows expand to show the verbatim transcript snippet.
const UpcomingAppointmentsCard = ({ domainId, site, days }) => {
  const { user } = useAuth();
  const isManager = !!user && MANAGER_SET.has(user.role);
  const [countData, setCountData] = useState(null);
  const [listData, setListData] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [open, setOpen] = useState(null);

  const setOutcome = async (apptId, outcome) => {
    try {
      await axios.patch(`${API}/analytics/appointments/${apptId}/outcome`, { outcome });
      setListData((p) => ({
        ...p,
        agents: (p?.agents || []).map((a) => ({
          ...a,
          appointments: a.appointments.map((ap) => (ap.id === apptId ? { ...ap, outcome } : ap)),
        })),
      }));
      toast.success(outcome ? `Marked ${outcome === 'shown' ? 'shown' : 'no-show'}` : 'Outcome cleared');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save outcome');
    }
  };

  useEffect(() => {
    const base = new URLSearchParams();
    if (domainId) base.set('domain_id', domainId);
    if (site) base.set('site', site);
    const countP = new URLSearchParams(base); countP.set('days', String(days || 7));
    const listP = new URLSearchParams(base); listP.set('days', '7');
    Promise.all([
      axios.get(`${API}/analytics/upcoming-appointments?${countP}`),
      axios.get(`${API}/analytics/upcoming-appointments?${listP}`),
    ])
      .then(([c, l]) => { setCountData(c.data); setListData(l.data); })
      .catch(e => { if (e?.response?.status === 403) setHidden(true); });
  }, [domainId, site, days]);

  if (hidden) return null;
  const rows = (listData?.agents || []).flatMap(a =>
    a.appointments.map(ap => ({ ...ap, agent_name: a.agent_name }))
  ).sort((x, y) => (x.when || '').localeCompare(y.when || '')).slice(0, 5);

  return (
    <Card data-testid="upcoming-appointments-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center justify-between">
          <span className="flex items-center gap-2"><CalendarClock className="w-4 h-4" /> Upcoming Appointments</span>
          <span className="text-2xl font-bold" data-testid="upcoming-appointments-count">{countData?.total ?? '—'}</span>
        </CardTitle>
        <p className="text-xs text-slate-400">
          Next {days || 7} days{site ? ` · ${site}` : ''} — extracted from call transcripts. List shows the next 7 days.
        </p>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {rows.length === 0 && <p className="text-sm text-slate-400 italic" data-testid="upcoming-appointments-empty">No appointments in the next 7 days.</p>}
        {rows.map((r, i) => (
          <div key={i} className="border border-slate-100 rounded-lg" data-testid={`upcoming-appointment-row-${i}`}>
            <button
              className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-left"
              onClick={() => setOpen(open === i ? null : i)}
              data-testid={`upcoming-appointment-toggle-${i}`}
            >
              <span className="text-xs">
                <span className="font-semibold text-slate-700">{fmtWhen(r.when)}</span>
                <span className="text-slate-500 ml-2">{r.agent_name}</span>
                {r.type && <span className="ml-2 text-[10px] uppercase tracking-wider text-emerald-600">{r.type}</span>}
                {r.outcome && (
                  <span className={`ml-2 text-[10px] font-bold uppercase tracking-wider ${r.outcome === 'shown' ? 'text-emerald-700' : 'text-red-500'}`}
                    data-testid={`appointment-outcome-badge-${i}`}>
                    {r.outcome === 'shown' ? '✓ Shown' : '✕ No-show'}
                  </span>
                )}
              </span>
              {open === i ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
            </button>
            {open === i && (
              <div className="px-3 pb-2 text-xs text-slate-500" data-testid={`upcoming-appointment-snippet-${i}`}>
                <p className="italic border-l-2 border-emerald-300 pl-2">"{r.snippet || 'No snippet stored'}"</p>
                <p className="text-[10px] text-slate-400 mt-1">Said as: "{r.when_text}" · confidence {Math.round((r.confidence || 0) * 100)}% · source: {r.source}</p>
                {isManager && r.id && (
                  <div className="flex items-center gap-1.5 mt-2" data-testid={`appointment-outcome-actions-${i}`}>
                    <button onClick={() => setOutcome(r.id, r.outcome === 'shown' ? null : 'shown')}
                      className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${r.outcome === 'shown' ? 'bg-emerald-600 border-emerald-600 text-white' : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'}`}
                      data-testid={`appointment-shown-btn-${i}`}>✓ Shown</button>
                    <button onClick={() => setOutcome(r.id, r.outcome === 'no_show' ? null : 'no_show')}
                      className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${r.outcome === 'no_show' ? 'bg-red-500 border-red-500 text-white' : 'border-red-300 text-red-600 hover:bg-red-50'}`}
                      data-testid={`appointment-noshow-btn-${i}`}>✕ No-show</button>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default UpcomingAppointmentsCard;
